import { Link } from 'react-router-dom'
import { CONTACT_STATUSES, STATUS_LABELS } from '../constants/taxonomy'
import { STATUS_COLORS } from '../components/map/confidenceStyle'
import { usePageMeta } from '../hooks/usePageMeta'

const STATUS_DESCRIPTIONS: Record<(typeof CONTACT_STATUSES)[number], string> = {
  unconfirmed: 'A single sighting has come in. Shown as a large, faint, fuzzy-edged circle — real, but not yet corroborated by anyone else.',
  corroborated: 'At least two independent people have reported something matching in the same place and timeframe. The circle tightens and darkens.',
  high_confidence: 'Multiple independent reports, including from higher-trust accounts, agree closely enough that this is very likely a genuine event.',
  officially_declared: 'Either an admin has confirmed it, or it matches an official detection already published by government sources — shown as a small, solid, ringed marker.',
}

export default function AboutPage() {
  usePageMeta(
    'About',
    'How the fusion/confidence model works: reports are clustered by species, location, and time, and corroborated sightings raise a contact from unconfirmed to officially declared.',
    '/about',
  )

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
          ← Back to map
        </Link>

        <div className="mt-4 mb-8">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">About Biosecurity Reporter</h1>
          <p className="mt-1 text-sm text-gray-500">
            Crowdsourced situational awareness for the H5N1 avian influenza outbreak in Australia.
          </p>
        </div>

        <div className="rounded-xl border-2 border-red-300 bg-red-50 px-5 py-4 mb-8">
          <p className="text-sm font-semibold text-red-800 mb-1">
            This is not a substitute for official biosecurity reporting.
          </p>
          <p className="text-sm text-red-700">
            If you suspect an emergency animal disease, call the Emergency Animal Disease Watch
            Hotline on <span className="font-semibold">1800 675 888</span> (free, 24/7) — every time,
            regardless of what you report here. Nothing submitted on this platform is sent to DAFF or
            any state agency, and no report here is a formal notification.
          </p>
        </div>

        <section className="mb-8">
          <h2 className="text-base font-semibold text-gray-900 mb-2">What this platform does</h2>
          <div className="text-sm text-gray-700 space-y-3">
            <p>
              Official reporting channels are accurate but slow — by the time DAFF or a state agency
              formally confirms and publishes a detection, people nearby have often already noticed
              warning signs with no way to see what their neighbours are seeing. This platform closes
              that visibility gap, not the reporting one.
            </p>
            <p>
              Any registered person can report a potential sighting — a dead or sick bird, an unusual
              die-off — in under a minute. On its own, one report is low-confidence: it could be
              anything. The value comes from <strong>fusion</strong>: a background process
              continuously clusters and corroborates reports by species, location, and time, so that
              independent sightings of the same real-world event reinforce each other and the picture
              sharpens automatically as more people report.
            </p>
            <p>
              This is a situational-awareness layer that can <em>inform</em> official biosecurity
              response — giving officers and farmers earlier, richer visibility into where activity is
              clustering — never a replacement for it.
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-base font-semibold text-gray-900 mb-3">How confidence builds</h2>
          <ul className="space-y-3">
            {CONTACT_STATUSES.map((status) => (
              <li key={status} className="flex gap-3">
                <span
                  className="mt-1 h-3 w-3 rounded-full shrink-0"
                  style={{ backgroundColor: STATUS_COLORS[status] }}
                  aria-hidden
                />
                <div className="text-sm">
                  <span className="font-medium text-gray-900">{STATUS_LABELS[status]}</span>
                  <p className="text-gray-600">{STATUS_DESCRIPTIONS[status]}</p>
                </div>
              </li>
            ))}
          </ul>
          <p className="text-sm text-gray-600 mt-3">
            Not every voice counts equally: reports from verified individuals and admin-approved
            professionals (vets, farmers, biosecurity officers) carry more weight than unverified
            accounts, and repeat reports from the same account are down-weighted so one person can't
            single-handedly push a sighting to high confidence.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-base font-semibold text-gray-900 mb-2">Integrity</h2>
          <p className="text-sm text-gray-700">
            Every report and status change is recorded on a tamper-evident ledger — each entry
            is cryptographically linked to the one before it, so nothing can be silently
            altered or deleted after the fact. Anyone can{' '}
            <Link to="/ledger/verify" className="text-blue-600 hover:text-blue-700 font-medium">
              independently verify the whole ledger
            </Link>, and any contact's own history is visible from its detail panel on the map.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-base font-semibold text-gray-900 mb-2">Privacy</h2>
          <p className="text-sm text-gray-700">
            Contact details shown on the map never reveal who reported what — only aggregate counts
            (e.g. "4 reports, 1 verified professional, last 3 days"). Individual reporter identity is
            never exposed publicly. Full details in our{' '}
            <Link to="/privacy" className="text-blue-600 hover:text-blue-700 font-medium">Privacy Policy</Link>
            {' '}and{' '}
            <Link to="/terms" className="text-blue-600 hover:text-blue-700 font-medium">Terms of Service</Link>.
          </p>
        </section>

        <Link to="/" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
          ← Back to map
        </Link>
      </div>
    </div>
  )
}
